// =============================================================================
// Photos on calendar events, backed by the private `event-attachments`
// bucket (supabase/event_attachments.sql). Two places use them:
//   event.attachments            — pictures attached to the event itself
//   event.comments[].attachments — pictures sent in the comment thread
// A record is { id, path, name, type, width, height }; only `path` is
// durable, the app signs a URL each time it shows the picture.
//
// Paths encode who may see the file (personal/<uid>/… or shared/<cid>/…),
// which is what the storage policies check.
// =============================================================================

import { supabase } from './supabase';

const BUCKET = 'event-attachments';
const SIGNED_URL_TTL_SECONDS = 60 * 60 * 24;
// Phone photos are 3–12 MB; the thread only ever shows them at screen size.
const MAX_EDGE = 1600;
const JPEG_QUALITY = 0.85;

const newId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

export const attachmentFolder = (event, userId) =>
  event?.sharedCalendarId ? `shared/${event.sharedCalendarId}` : `personal/${userId}`;

// Shrink a picture on the device before it goes up. Falls back to the
// original file when the browser cannot decode it (HEIC on the web, say).
export async function shrinkImage(file) {
  if (typeof createImageBitmap !== 'function' || typeof document === 'undefined') return file;
  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height));
    const width = Math.round(bitmap.width * scale);
    const height = Math.round(bitmap.height * scale);
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    canvas.getContext('2d').drawImage(bitmap, 0, 0, width, height);
    bitmap.close?.();
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY));
    if (!blob) return file;
    const name = (file.name || 'photo').replace(/\.[^.]+$/, '') + '.jpg';
    return Object.assign(blob, { name, width, height });
  } catch {
    return file;
  }
}

// Uploads every file on its own so one failure keeps the others.
// Returns { attachments, failedCount }.
export async function uploadEventPhotos(folder, eventId, files) {
  const attachments = [];
  let failedCount = 0;
  for (const original of files) {
    const f = await shrinkImage(original);
    const id = newId();
    const ext = (f.type === 'image/jpeg' ? 'jpg' : (f.name || '').split('.').pop() || 'jpg').toLowerCase();
    const path = `${folder}/${eventId}/${id}.${ext}`;
    try {
      const { error } = await supabase.storage.from(BUCKET).upload(path, f, {
        contentType: f.type || 'image/jpeg',
        upsert: false
      });
      if (error) throw error;
      attachments.push({
        id,
        path,
        name: original.name || `${id}.${ext}`,
        type: f.type || 'image/jpeg',
        width: f.width || null,
        height: f.height || null
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[eventAttachments] upload failed for', original.name, err);
      failedCount += 1;
    }
  }
  return { attachments, failedCount };
}

// Best-effort; never throws. Used when a picture is removed from an event.
export async function deleteEventPhotos(paths) {
  const valid = (paths || []).filter(Boolean);
  if (valid.length === 0) return;
  try {
    const { error } = await supabase.storage.from(BUCKET).remove(valid);
    if (error) throw error;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[eventAttachments] delete failed', err);
  }
}

// { path: signedUrl } for the paths that could be signed.
export async function signEventPhotos(paths) {
  const map = {};
  const valid = [...new Set((paths || []).filter(Boolean))];
  if (valid.length === 0) return map;
  try {
    const { data, error } = await supabase.storage
      .from(BUCKET)
      .createSignedUrls(valid, SIGNED_URL_TTL_SECONDS);
    if (error) throw error;
    (data || []).forEach((d, i) => {
      if (d && !d.error && d.signedUrl) map[valid[i]] = d.signedUrl;
    });
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[eventAttachments] signing failed', err);
  }
  return map;
}

// Every photo path an event carries (its own and its comments').
export const eventPhotoPaths = (ev) => [
  ...((ev?.attachments || []).map((a) => a.path)),
  ...((ev?.comments || []).flatMap((c) => (c.attachments || []).map((a) => a.path)))
];
